import React, { useState } from 'react';
import { DollarSignIcon, InfoIcon, BookIcon, CalendarIcon } from 'lucide-react';
import { Budget } from './BudgetManagement';
import { BudgetStatusBadge } from './BudgetStatusBadge';
import { BudgetRules } from './BudgetRules';
import { formatCurrency } from '../../utils/currencyFormatter';
interface BudgetCardProps {
  budget: Budget;
  locale: string;
}
export function BudgetCard({
  budget,
  locale
}: BudgetCardProps) {
  // Calculate percentage of budget used
  const percentUsed = Math.min(100, Math.round((budget.totalAmount - budget.remainingAmount) / budget.totalAmount * 100));
  const percentRemaining = 100 - percentUsed;
  // Determine color based on remaining percentage
  const getProgressColor = () => {
    if (percentRemaining > 50) return 'bg-green-500';
    if (percentRemaining > 20) return 'bg-yellow-500';
    return 'bg-red-500';
  };
  // Format dates
  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString(locale, {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };
  return <div className="bg-white rounded-lg overflow-hidden" data-id="element-8537">
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6" data-id="element-8538">
        <div className="md:col-span-2" data-id="element-8539">
          <div className="space-y-4" data-id="element-8540">
            <div data-id="element-8541">
              <h4 className="text-lg font-medium text-gray-900" data-id="element-8542">
                {budget.name}
              </h4>
              {budget.description && <p className="mt-1 text-sm text-gray-500" data-id="element-8543">
                  {budget.description}
                </p>}
            </div>
            <div className="grid grid-cols-2 gap-4" data-id="element-8544">
              <div data-id="element-8545">
                <h5 className="text-xs font-medium text-gray-500 uppercase tracking-wide flex items-center" data-id="element-8546">
                  <BookIcon className="h-3 w-3 mr-1" data-id="element-8547" />
                  Budget Type
                </h5>
                <p className="mt-1 text-sm text-gray-900" data-id="element-8548">
                  {budget.budgetTypeName}
                </p>
              </div>
              <div data-id="element-8549">
                <h5 className="text-xs font-medium text-gray-500 uppercase tracking-wide flex items-center" data-id="element-8550">
                  <CalendarIcon className="h-3 w-3 mr-1" data-id="element-8551" />
                  Period
                </h5>
                <p className="mt-1 text-sm text-gray-900" data-id="element-8552">
                  {formatDate(budget.startDate)} to {formatDate(budget.endDate)}
                </p>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4" data-id="element-8553">
              <div data-id="element-8554">
                <h5 className="text-xs font-medium text-gray-500 uppercase tracking-wide" data-id="element-8555">
                  GL Code
                </h5>
                <p className="mt-1 text-sm font-mono text-gray-900" data-id="element-8556">
                  {budget.glCode}
                </p>
              </div>
              <div data-id="element-8557">
                <h5 className="text-xs font-medium text-gray-500 uppercase tracking-wide" data-id="element-8558">
                  Entity / Site
                </h5>
                <p className="mt-1 text-sm text-gray-900" data-id="element-8559">
                  {budget.entityName || 'Global'}
                  {budget.siteName && <span className="text-gray-500" data-id="element-8560"> / {budget.siteName}</span>}
                </p>
              </div>
            </div>
            <div data-id="element-8561">
              <h5 className="text-xs font-medium text-gray-500 uppercase tracking-wide flex items-center" data-id="element-8562">
                <DollarSignIcon className="h-3 w-3 mr-1" data-id="element-8563" />
                Budget Status
              </h5>
              <div className="mt-2 flex items-center" data-id="element-8564">
                <BudgetStatusBadge status={budget.status} data-id="element-8565" />
                <span className="ml-2 text-sm text-gray-500" data-id="element-8566">
                  Last updated:{' '}
                  {new Date(budget.updatedAt).toLocaleDateString(locale, {
                  year: 'numeric',
                  month: 'short',
                  day: 'numeric'
                })}
                </span>
              </div>
            </div>
          </div>
        </div>
        <div className="md:col-span-1" data-id="element-8567">
          <div className="space-y-4" data-id="element-8568">
            <div data-id="element-8569">
              <h5 className="text-xs font-medium text-gray-500 uppercase tracking-wide" data-id="element-8570">
                Budget Amount
              </h5>
              <div className="mt-1" data-id="element-8571">
                <div className="text-2xl font-semibold text-gray-900" data-id="element-8572">
                  {formatCurrency(budget.remainingAmount, budget.currency, locale)}
                </div>
                <div className="text-sm text-gray-500" data-id="element-8573">
                  remaining of{' '}
                  {formatCurrency(budget.totalAmount, budget.currency, locale)}
                </div>
              </div>
            </div>
            <div data-id="element-8574">
              <div className="flex justify-between text-sm" data-id="element-8575">
                <span data-id="element-8576">{percentRemaining}% remaining</span>
                <span data-id="element-8577">{percentUsed}% used</span>
              </div>
              <div className="mt-1 w-full bg-gray-200 rounded-full h-2.5" data-id="element-8578">
                <div className={`h-2.5 rounded-full ${getProgressColor()}`} style={{
                width: `${percentRemaining}%`
              }} data-id="element-8579"></div>
              </div>
            </div>
            <div className="mt-4" data-id="element-8580">
              <h5 className="text-xs font-medium text-gray-500 uppercase tracking-wide flex items-center" data-id="element-8581">
                <InfoIcon className="h-3 w-3 mr-1" data-id="element-8582" />
                Budget Rules
              </h5>
              <div className="mt-2" data-id="element-8583">
                <BudgetRules rulesJson={budget.rules} data-id="element-8584" />
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>;
}